import {
  Box,
  InputProps as MuiInputProps,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  ChangeEventHandler,
  ReactElement,
  Ref,
  forwardRef,
  memo,
  useCallback,
} from 'react';
import { useIntl } from 'react-intl';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { getHelperTextFromError, getLocalizedLabel } from './common';
import { NumberFormatCustom } from './numberField/helpers';
import { SimpleTextInputProps } from './types';
import { HoverWrapper } from 'components/user/card/hoverWrapper';

function SimpleTextField(
  {
    value,
    localisedLabel,
    labelId,
    required,
    fieldName,
    disabled,
    handleChange,
    error,
    minRows,
    type,
    sx,
    onBlur,
    dense,
    reserveSpaceForHelperText,
    onHoverComponent,
    startIcon,
    endIcon,
  }: SimpleTextInputProps<string>,
  ref: Ref<HTMLDivElement>
): ReactElement {
  const intl = useIntl();

  const helperText = getHelperTextFromError(error, intl);
  const label = getLocalizedLabel(intl, localisedLabel, labelId);

  const unifiedHandleChange: ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = useCallback(
    (event): void => {
      handleChange(event, event.target.value);
    },
    [handleChange]
  );

  const InputPropsObj: Partial<MuiInputProps> = {};

  if (type === 'number') {
    InputPropsObj.inputComponent = NumberFormatCustom;
    InputPropsObj.inputProps = { decimalScale: 2, thousandSeparator: '.' };
  }

  if (startIcon) {
    InputPropsObj.startAdornment = (
      <InputAdornment position='start'>{startIcon}</InputAdornment>
    );
  }

  if (endIcon) {
    InputPropsObj.endAdornment = (
      <InputAdornment position='end'>{endIcon}</InputAdornment>
    );
  }

  if (onHoverComponent) {
    sx = sx
      ? { ...sx, cursor: 'pointer' as const }
      : { cursor: 'pointer' as const };
  }

  const isDisabled = Boolean(disabled);

  const textField = (
    <TextField
      name={fieldName}
      multiline
      fullWidth
      minRows={minRows}
      required={required}
      disabled={isDisabled}
      data-qa={`form-field-textField-${fieldName}`}
      autoComplete='off'
      id={`text-input-${fieldName}`}
      label={label}
      margin={dense ? 'dense' : 'none'}
      variant='outlined'
      value={value ?? ''}
      onChange={unifiedHandleChange}
      onBlur={onBlur}
      error={Boolean(error)}
      helperText={helperText || (reserveSpaceForHelperText && ' ')}
      InputProps={InputPropsObj}
      sx={sx}
    />
  );

  if (onHoverComponent && startIcon) {
    return (
      <Box sx={{ position: 'relative' }}>
        {textField}
        <Box
          sx={{
            position: 'absolute',
            top: dense ? 8 : 0,
            left: 0,
            bottom: dense ? 28 : 0,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '14px',
            paddingRight: '8px',
            width: 'fit-content',
            maxWidth: '100%',
            pointerEvents: 'none',
          }}
        >
          <HoverWrapper offset={[0, 8]} onHoverComponent={onHoverComponent}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                pointerEvents: 'auto',
              }}
            >
              <Box sx={{ opacity: 0 }}>{startIcon}</Box>
              <Box component='span' sx={{ opacity: 0 }}>{value}</Box>
            </Box>
          </HoverWrapper>
        </Box>
      </Box>
    );
  }

  if (onHoverComponent) {
    return (
      <HoverWrapper offset={[0, -40]} onHoverComponent={onHoverComponent}>
        {textField}
      </HoverWrapper>
    );
  }

  return (
    <Tooltip
      title={
        typeof disabled === 'object'
          ? formatTranslation(intl, unsafeTranslationKey(disabled.reason) )
          : undefined
      }
    >
      {textField}
    </Tooltip>
  );
}

export const MemoSimpleTextField = memo(forwardRef(SimpleTextField));
